import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import AdminDentalChartTab from '../components/AdminDentalChartTab';
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  Settings,
  LogOut,
  Menu,
  X,
  Home,
  Sparkles,
  Smile
} from 'lucide-react';

export default function DentalChartDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isAdmin, canUseDentalChart } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { icon: LayoutDashboard, label: 'Overview', path: '/dashboard' },
    { icon: Sparkles, label: 'AI Estimate', path: '/dashboard/ai-estimate', isNew: true },
    { icon: FileText, label: 'Cost Estimates', path: '/dashboard/estimates' },
    { icon: Users, label: 'Referrals', path: '/dashboard/referrals' },
    { icon: Building2, label: 'Business Network', path: '/dashboard/businesses' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dental-chart-dashboard">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-100 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <div>
                <span className="font-semibold text-primary text-sm">MED CONNECT</span>
                <span className="text-accent font-light ml-1 text-sm">ALBANIA</span>
              </div>
            </Link>
          </div>

          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <span className="text-accent font-semibold">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Menu</p>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path) ? 'bg-accent/10 text-accent' : 'text-muted-foreground hover:bg-slate-50 hover:text-primary'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {item.isNew && <Badge className="ml-auto bg-accent text-white text-xs">NEW</Badge>}
              </Link>
            ))}

            {canUseDentalChart && (
              <div className="pt-4 mt-4 border-t border-slate-100">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Tools</p>
                <Link
                  to="/dashboard/dental-chart"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium bg-accent/10 text-accent"
                  data-testid="nav-dental-chart"
                >
                  <Smile className="w-5 h-5" />
                  Dental Chart
                </Link>
              </div>
            )}

            {isAdmin && (
              <div className="pt-4 mt-4 border-t border-slate-100">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Admin</p>
                <Link
                  to="/admin"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-50 hover:text-primary"
                  data-testid="nav-admin"
                >
                  <Settings className="w-5 h-5" />
                  Admin Panel
                </Link>
              </div>
            )}
          </nav>

          <div className="p-4 border-t border-slate-100">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <header className="bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 hover:bg-slate-100 rounded-lg">
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-primary">Dental Chart</h1>
              <p className="text-xs text-muted-foreground">Create and manage dental estimates</p>
            </div>
          </div>
          <Link to="/" className="p-2 hover:bg-slate-100 rounded-lg" data-testid="home-link">
            <Home className="w-5 h-5 text-muted-foreground" />
          </Link>
        </header>

        <div className="p-4 md:p-6">
          <AdminDentalChartTab />
        </div>
      </main>
    </div>
  );
}
